import React, { useState, useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import s from './Nav.module.scss';
import NavMenuItem from './components/NavMenuItem';
import SubMenu from './components/SubMenu';
import StoreList from './components/StoreList';
import AppDownloadArea from './components/AppDownloadArea';
import ThirdLevelNav from './components/ThirdLevelNav';
import { buildNavItems, getProductInfo, getAppDownloadLinks } from './utils/navUtils';
import { getMenuItems } from './constant';

/**
 * PC端导航组件
 * @param {Object} props - 组件属性
 * @param {string} props.locale - 当前语言
 * @param {boolean} props.navPosition - 导航位置
 * @param {string} props.path - 当前路径
 */
const Nav = (props) => {
  const { locale, navPosition, path } = props;
  const navRef = useRef(null);
  
  // 从 Redux 获取 goodLinks
  const goodLinks = useSelector(state => state.goodLinks);
  
  // 状态管理
  const [isHide] = useState(true);
  const [purchaseHide, setPurchaseHide] = useState(true);
  const [showShopList, setShowShopList] = useState(false);
  
  // 路径解析
  const pathArr = path.split('/');
  
  // 处理导航显示样式
  let nav = s.nav;
  let airSecondSub = s.hide;
  let thirdSub = s.third;
  let thirdSubSpare = s.hide;
  
  // 处理应用下载页面
  let isAir = false;
  
  // 隐藏三级导航 并且固定导航]
  const hideThirdAndFixedNavPaths = ['/', '/comparison-temp-rh-monitor', '/comparison-air-monitor'];
  if (hideThirdAndFixedNavPaths.includes(path)) {
    nav = s.navFixed;
    thirdSub = s.hide;
  }

   // 处理不显示三级导航的情况
   const hideThirdSubPaths = ['mi-multifunction-air-monitor', 'mi-air-monitor', 'store', 'about-us', 'privacy'];
   if (hideThirdSubPaths.includes(pathArr[1])) {
     thirdSub = s.hide;
   }
  
  // 隐藏三级导航 并且显示空气监测器二级导航
  if (path === '/home-app' || path === '/air') {
    thirdSub = s.hide;
    airSecondSub = s.airSecond;
    isAir = true;
  }
  
  if (path === '/lee-guitars/app') {
    thirdSub = s.hide;
    airSecondSub = s.airSecond;
  }
  
  // 构建导航项（去除二级项抽象，二级激活在本地按路径判断）
  const { firstLevelItems, thirdLevelItems } = buildNavItems(null, pathArr, s);
  
  // 处理导航位置变化
  if (navPosition) {
    if (pathArr[1] === 'home-app' || pathArr[1] === 'air') {
      airSecondSub = s.airSecondSubPosition;
    } else if (pathArr[1] !== 'mi-multifunction-air-monitor') {
      thirdSub = s.thirdSubPosition;
      thirdSubSpare = s.third;
    }
  }
  
  // 获取产品信息
  const { url, nameId } = getProductInfo(pathArr);
  
  // 处理购买弹窗
  const purchaseName = !purchaseHide ? s.purchaseShow : s.purchaseHide;
  
  // 获取下载链接
  const downloadLinks = getAppDownloadLinks(isAir);
  
  // 获取商店数据 - 直接使用 Redux 中的 goodLinks
  const goodsShops = goodLinks?.[pathArr[1]]?.shops || [];
  const isSoldOut = goodLinks?.[pathArr[1]]?.status === 'off' || false;
  const isInquiry = goodLinks?.[pathArr[1]]?.status === 'inquiry' || false;
  const getJumpLink = goodsShops?.filter(item => item.tag) || [];
  const allMalls = goodLinks?.['all-mall']?.shops || [];
  
  // 处理购买点击
  const handlePurchaseClick = (e) => {
    e.stopPropagation();
    setPurchaseHide(!purchaseHide);
  };
  
  // 滚动到顶部
  const toTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  // 根据路径返回激活样式类
  const activeClass = (slug) => (pathArr[1] === slug ? s.active : null);

  // 获取菜单项配置
  const { temperatureItems, clockItems, airItems, sensorItems } = getMenuItems(activeClass, s);

  return (
    <div className={nav} ref={navRef}>
      <div className={s.first_sub}>
        <ul
          className={s.items}
          onMouseLeave={() => {
            showShopList && setShowShopList(false);
          }}
        >
          {/* Logo */}
          <li>
            <div className={s.logo}>
              <NavLink to="/" className={firstLevelItems[0]}>
                <i />
              </NavLink>
            </div>
          </li>
          
          {/* 温湿度监测器 */}
          <li
            className={locale.indexOf('US') > -1 ? s.tempHum_en : s.tempHum}
            style={{ margin: 0, paddingLeft: 30, paddingRight: 30 }}
          >
            <span className={firstLevelItems[1]}>
              <FormattedMessage id="temp_rh" />
              <i className={s.line} />
            </span>
            <SubMenu
              items={temperatureItems}
              className={locale.indexOf('US') > -1 ? s.tempHumHide_en : s.tempHumHide}
              styles={s}
            />
          </li>
          
          {/* 时钟 */}
          <NavMenuItem
            messageId="clock"
            isActive={firstLevelItems[7]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={clockItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 空气监测器 */}
          <NavMenuItem
            messageId="air_name"
            isActive={firstLevelItems[2]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={airItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 传感器 */}
          <NavMenuItem
            messageId="sensor_name"
            isActive={firstLevelItems[8]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={sensorItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 蓝牙网关 */}
          <NavMenuItem
            linkTo="/bluetooth-gateway/overview"
            messageId="gateway"
            isActive={firstLevelItems[6]}
            styles={s}
          />
          
          {/* 青萍应用 */}
          <NavMenuItem
            linkTo="/home-app"
            messageId="cg_app"
            isActive={firstLevelItems[3]}
            styles={s}
          />
          
          {/* 青萍IoT */}
          <li className={firstLevelItems[4]}>
            <span>
              <a
                href="https://qingpingiot.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="qing_ping_iot" />
              </a>
              <i className={s.line} />
            </span>
          </li>
          
          {/* 开发者 */}
          <li className={firstLevelItems[5]}>
            <span className={s.store}>
              <a
                href="https://developer.qingping.co/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="developer" />
              </a>
              <i className={s.line} />
            </span>
          </li>
          
          {/* 商城列表 */}
          <li
            className={firstLevelItems[5]}
            onMouseOver={() => setShowShopList(true)}
          >
            <span className={s.store}>
              <FormattedMessage id="store" />
              <i className={s.line} />
            </span>
          </li>
          
          <div
            className={s.shopArrow}
            onMouseOver={() => setShowShopList(true)}
          >
            <div style={{ height: 65, width: 28, marginTop: -46 }}></div>
            <StoreList
              stores={allMalls}
              isVisible={showShopList}
              locale={locale}
              styles={s}
            />
          </div>
        </ul>
        
        {/* 购买按钮 */}
        <div className={s.buy}>
          <a
            href="https://item.mi.com/product/7303.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="buy" />
          </a>
        </div>
      </div>
      
      {/* 应用下载区域 */}
      <div className={airSecondSub}>
        <AppDownloadArea
          isAir={isAir}
          iosDownloadUrl={downloadLinks.ios}
          androidDownloadUrl={downloadLinks.android}
          googlePlayUrl={downloadLinks.googlePlay}
          locale={locale}
          showQrCode={!isHide}
          styles={s}
        />
      </div>
      
      {/* 三级导航 */}
      <div className={thirdSub}>
        <ThirdLevelNav
          url={url}
          productName={<FormattedMessage id={nameId} values={nameId === 'frog' || nameId === 'magpie' || nameId === 'hodor_name' || nameId === 'parrot_name' || nameId === 'chicken' || nameId === 'robb' ? { br: '' } : {}} />}
          activeItems={thirdLevelItems}
          onItemClick={toTop}
          isSoldOut={isSoldOut || !goodLinks || (!getJumpLink?.length && !goodsShops?.length)}
          isInquire={isInquiry}
          locale={locale}
          getJumpLink={getJumpLink}
          goodsShops={goodsShops}
          buyText={<FormattedMessage id="buy" />}
          styles={s}
        />
      </div>
      <div className={thirdSubSpare}></div>
      
      {/* 购买提示 */}
      <div className={purchaseName}>
        <div className={s.purchaseBg} onClick={handlePurchaseClick} />
        <i />
        <span>
          请发送邮件至{' '}
          <a
            href="mailto:bd@qingping.co"
            target="_blank"
            rel="noopener noreferrer"
          >
            bd@qingping.co
          </a>
          ，<br />
          或致电李先生 189 1187 7866 获取报价。
        </span>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  locale: state.language,
});

export default connect(mapStateToProps)(Nav); 